/**
 * Utility functions for handling DMM-specific column operations
 */

import { NewValueParams } from 'ag-grid-community';
import { BusinessSegmentNames, CorpSegmentNames } from 'src/constants/corp-segment-constants';
import {
  BUSINESS_SEGMENT_OPTIONAL_FIELDS,
  DMM_BUSINESS_SEGMENT_ORDER,
  DMM_FIELDS_MAPPING,
  ECHO_BUSINESS_SEGMENT_OPTIONAL_FIELDS,
  ECHO_PRODUCT_LINE_CODE
} from 'src/constants/business-group-constants';
import { MasterBusinessSegments } from 'src/models/AppContextModels';
import { CountryCompanyMapping, DMMBusinessSegmentType, DMMProductLineCampaignFlatten } from 'src/models/DmmModel';

/**
 * Gets unique dropdown values for DMM business segments based on campaign data
 *
 * @param filteredCampaigns - Array of campaign data to extract dropdown values from
 * @param businessSegmentName - The name of the business segment to get values for
 * @returns Array of unique, sorted dropdown values
 */
export const getDMMMasterSegmentDropDowns = (filteredCampaigns: DMMProductLineCampaignFlatten[], businessSegmentName: string): string[] => {
  if (!filteredCampaigns?.length) {
    return [];
  }

  const field = DMM_FIELDS_MAPPING[businessSegmentName as keyof typeof DMM_FIELDS_MAPPING];

  if (!field) {
    return [];
  }

  return getUniqueStringValues(filteredCampaigns.map((campaign) => campaign[field]));
};

/**
 * Extracts unique, non-empty string values from an array and sorts them
 *
 * @param values - Array of values to filter and deduplicate
 * @returns Array of unique, sorted string values
 */
const getUniqueStringValues = (values: any[]): string[] => {
  return [
    ...new Set(values.filter((value): value is string => value !== null && value !== undefined && typeof value === 'string' && value.trim() !== ''))
  ].sort();
};

/**
 * Filters campaigns based on previously selected values in the row
 *
 * @param campaigns - Array of all available campaigns
 * @param rowData - Current row data containing selected values
 * @param currentField - The current business segment field being processed
 * @returns Filtered array of campaigns matching previous selections
 */
export const getFilteredCampaigns = (
  campaigns: DMMProductLineCampaignFlatten[],
  rowData: any,
  currentField: DMMBusinessSegmentType
): DMMProductLineCampaignFlatten[] => {
  if (!campaigns?.length) {
    return [];
  }

  let filteredCampaigns = [...campaigns];
  const currentIndex = DMM_BUSINESS_SEGMENT_ORDER.indexOf(currentField);

  // Apply filters based on previous selections
  for (let i = 0; i < currentIndex; i++) {
    const previousField = DMM_BUSINESS_SEGMENT_ORDER[i];
    const selectedValue = rowData[previousField];

    if (selectedValue) {
      const campaignField = DMM_FIELDS_MAPPING[previousField];
      filteredCampaigns = filteredCampaigns.filter((campaign) => campaign[campaignField] === selectedValue);
    }
  }

  return filteredCampaigns;
};

/**
 * Resets all subsequent business segment fields when a field value changes in DMM group
 *
 * @param params - The cell value change event parameters
 * @param currentField - The current field being changed
 * @param isDMMGroup - Boolean indicating if this is a DMM group
 */
export const resetSubsequentBusinessSegmentFields = (params: NewValueParams<any, any>, currentField: string, isDMMGroup: boolean): void => {
  // Early return if conditions aren't met
  if (!isDMMGroup || !DMM_BUSINESS_SEGMENT_ORDER.includes(currentField as DMMBusinessSegmentType) || !params.node) {
    return;
  }

  const currentIndex = DMM_BUSINESS_SEGMENT_ORDER.indexOf(currentField as DMMBusinessSegmentType);

  // Return if this is the last field in the order
  if (currentIndex >= DMM_BUSINESS_SEGMENT_ORDER.length - 1) {
    return;
  }

  // Get all fields that come after the current field
  const fieldsToReset = DMM_BUSINESS_SEGMENT_ORDER.slice(currentIndex + 1);

  // Reset each subsequent field to null
  fieldsToReset.forEach((field) => {
    params.node?.setDataValue(field, null);
  });

  // Refresh the cells to update edit state
  params.api.refreshCells({
    force: true,
    columns: fieldsToReset
  });
};

/**
 * Checks if all mandatory fields are filled for a given row of data based on product line
 *
 * @param data - The row data object containing business segment fields
 * @param productLine - The product line code to determine which fields are optional
 * @returns True if all mandatory fields are filled, false otherwise
 */
export const areAllMandatoryFieldsFilled = (data: any, productLine: string): boolean => {
  if (!data || !productLine) {
    return false;
  }

  const optionalFields = productLine === ECHO_PRODUCT_LINE_CODE ? ECHO_BUSINESS_SEGMENT_OPTIONAL_FIELDS : BUSINESS_SEGMENT_OPTIONAL_FIELDS;

  const mandatoryFields = DMM_BUSINESS_SEGMENT_ORDER.filter((field) => !optionalFields.includes(field));

  return mandatoryFields.every((field) => data[field] && data[field] !== '');
};

/**
 * Gets the default value for an optional field based on existing mandatory field values
 *
 * @param field - The optional field name to get the default value for
 * @param data - The current row data containing all business segment values
 * @param campaignsData - Array of campaign data to match against
 * @returns The default value if a unique match is found, null otherwise
 */
export const getDefaultOptionalValue = (field: DMMBusinessSegmentType, data: any, campaignsData: DMMProductLineCampaignFlatten[]): string | null => {
  if (!data || !campaignsData?.length) {
    return null;
  }

  const filteredCampaigns = campaignsData.filter((campaign) => {
    // Get all filled fields from the current data (excluding the current field)
    const filledFields = Object.entries(DMM_FIELDS_MAPPING).filter(([businessField]) => data[businessField] && businessField !== field);

    // Check if all filled fields match the campaign
    return filledFields.every(
      ([businessField, campaignField]) => campaign[campaignField as keyof DMMProductLineCampaignFlatten] === data[businessField]
    );
  });

  // Only return a value if exactly one matching campaign is found
  if (filteredCampaigns.length === 1) {
    const campaignField = DMM_FIELDS_MAPPING[field];
    return filteredCampaigns[0][campaignField as keyof DMMProductLineCampaignFlatten] as string;
  }

  return null;
};

/**
 * Retrieves the country code associated with a given company code
 *
 * @param companyCode - The company code to look up
 * @param countryCompanyMapping - Array of country-company mapping objects
 * @returns The corresponding country code if found, empty string otherwise
 */
export const getCountryCodeFromCompany = (companyCode: string, countryCompanyMapping: CountryCompanyMapping[]): string => {
  if (!companyCode || !countryCompanyMapping?.length) {
    return '';
  }

  const mapping = countryCompanyMapping.find((mapping) => mapping.company_code === companyCode);

  return mapping?.country_code || '';
};

// ---------------------------------------------------------------------------
// Pure helpers shared between the main Forecast Input grid and the upload
// preview grid. The main grid applies these by translating the resulting row
// diff into AG Grid `setDataValue()` mutations; the preview grid feeds the
// resulting row directly back into React state. Same source of truth for both.
// ---------------------------------------------------------------------------

/**
 * Immutably applies the DMM business-segment dependency chain to a row after
 * `editedField` changed:
 *   1. If the edited field is not an *optional* field, every field that comes
 *      after it in `DMM_BUSINESS_SEGMENT_ORDER` is reset to null.
 *   2. Once every mandatory field is filled, still-empty optional fields are
 *      auto-populated when a single campaign uniquely determines their value.
 *
 * No-op for non-DMM business groups (i.e., when the edited field is not in
 * `DMM_BUSINESS_SEGMENT_ORDER`).
 *
 * @param row The row being edited (not mutated).
 * @param editedField The field the user just changed.
 * @param campaignsData Full DMM campaign master list.
 * @returns A new row object with the cascade applied.
 */
export const applyDMMBusinessSegmentChain = (
  row: Record<string, any>,
  editedField: string,
  campaignsData: DMMProductLineCampaignFlatten[],
  countryCompanyMapping: CountryCompanyMapping[] = []
): Record<string, any> => {
  if (!DMM_BUSINESS_SEGMENT_ORDER.includes(editedField as DMMBusinessSegmentType)) {
    return row;
  }

  const productLine = row[CorpSegmentNames.PRODUCT_LINE];
  const optionalFields = productLine === ECHO_PRODUCT_LINE_CODE ? ECHO_BUSINESS_SEGMENT_OPTIONAL_FIELDS : BUSINESS_SEGMENT_OPTIONAL_FIELDS;
  const isEditedFieldOptional = optionalFields.includes(editedField as DMMBusinessSegmentType);

  const updatedRow: Record<string, any> = { ...row };

  // Step 1: Reset subsequent fields. Optional-field edits don't cascade — an
  // optional field can be corrected without invalidating downstream mandatory
  // context. Matches the main grid's onCellValueChanged behaviour.
  if (!isEditedFieldOptional) {
    const editedIndex = DMM_BUSINESS_SEGMENT_ORDER.indexOf(editedField as DMMBusinessSegmentType);
    const fieldsToReset = DMM_BUSINESS_SEGMENT_ORDER.slice(editedIndex + 1);
    fieldsToReset.forEach((field) => {
      updatedRow[field] = null;
    });
  }

  // Step 2: Progressive auto-fill through the DMM chain.
  // Walk forward from the field the user just edited and, for each downstream field
  // that is still unset, look at the campaigns still consistent with the row so far.
  // If exactly one distinct value survives for that field, fill it in and continue —
  // the newly-filled value may in turn collapse the next field. Stop the walk the
  // moment we hit a field with 0 or 2+ options (user must choose, or nothing matches).
  //
  // This is a superset of the earlier "auto-populate optional when a unique campaign
  // matches" rule: mandatory fields get auto-filled too, matching the UX request that
  // "if there is only 1 selection left, auto-fill it."
  const startIndex = DMM_BUSINESS_SEGMENT_ORDER.indexOf(editedField as DMMBusinessSegmentType) + 1;
  for (let i = startIndex; i < DMM_BUSINESS_SEGMENT_ORDER.length; i++) {
    const field = DMM_BUSINESS_SEGMENT_ORDER[i];
    if (updatedRow[field]) continue; // user or a prior auto-fill already set it

    const candidates = getUniqueCampaignValuesForField(field, updatedRow, campaignsData, countryCompanyMapping);
    if (candidates.length === 1) {
      updatedRow[field] = candidates[0];
    } else {
      // 0 candidates: nothing matches — user must fix the upstream choice.
      // 2+ candidates: user needs to pick.
      // Either way, stop propagating so we don't guess.
      break;
    }
  }

  // Step 2 handles both mandatory and optional fields uniformly, using the country-aware
  // shared filter chain. The prior legacy "safety-net" step that separately auto-filled
  // optionals via `getDefaultOptionalValue` was removed after CR-298375884 comment #12 —
  // it called into a non-country-aware path (`getDefaultOptionalValue` doesn't take
  // `countryCompanyMapping`), which could auto-fill a wrong-country value for optional
  // fields, defeating the fix that Step 2 already provides for mandatory fields.

  return updatedRow;
};

/**
 * Returns the distinct set of values available for `field` on campaigns that remain
 * consistent with every DMM-order field already filled on `row`. Used by
 * `applyDMMBusinessSegmentChain` to decide whether a downstream field's option set has
 * collapsed to a single value (and can be auto-filled).
 *
 * Applies the SAME filter chain as `getBusinessSegmentDropdownValues` — Product Line +
 * Company→country + Campaign Name + DMM_BUSINESS_SEGMENT_ORDER — so the auto-fill never
 * chooses a value the editor dropdown would refuse to offer (which would immediately fail
 * segment validation).
 */
const getUniqueCampaignValuesForField = (
  field: DMMBusinessSegmentType,
  row: Record<string, any>,
  campaignsData: DMMProductLineCampaignFlatten[],
  countryCompanyMapping: CountryCompanyMapping[]
): string[] => {
  if (!campaignsData?.length) return [];
  const campaignField = DMM_FIELDS_MAPPING[field];
  if (!campaignField) return [];

  const filteredCampaigns = filterCampaignsForRow(row, field, campaignsData, countryCompanyMapping);

  return getUniqueStringValues(filteredCampaigns.map((campaign) => campaign[campaignField as keyof DMMProductLineCampaignFlatten]));
};

/**
 * Shared campaign filter used by both the auto-fill path (`getUniqueCampaignValuesForField`)
 * and the editor-dropdown path (`getBusinessSegmentDropdownValues`). Kept aligned so
 * auto-fill and dropdown always see the same eligible campaign set for a given row.
 *
 * Filters campaigns by:
 *   - `is_active`
 *   - Product Line (corp segment) — required constraint
 *   - Company → country_code (via mapping) — narrows down when Company is set
 *   - Campaign Name — narrows down when set and we're not editing that field itself
 *   - Each DMM_BUSINESS_SEGMENT_ORDER field already filled UP TO (but not including)
 *     `currentField` — the dependency chain
 */
const filterCampaignsForRow = (
  row: Record<string, any>,
  currentField: DMMBusinessSegmentType | string | null,
  campaignsData: DMMProductLineCampaignFlatten[],
  countryCompanyMapping: CountryCompanyMapping[],
  excludeFields: ReadonlySet<string> = new Set()
): DMMProductLineCampaignFlatten[] => {
  const productLine = excludeFields.has(CorpSegmentNames.PRODUCT_LINE) ? null : row[CorpSegmentNames.PRODUCT_LINE];
  const company = excludeFields.has(CorpSegmentNames.COMPANY) ? null : row[CorpSegmentNames.COMPANY];
  // Distinguish three cases here — collapsing them all to a truthy check surfaced the wrong
  // filter behaviour in review:
  //   - `null`  → no Company selected → skip the country filter entirely.
  //   - `''`    → Company set but unmapped in `countryCompanyMapping` → the country filter
  //               must match campaigns whose `country_code` is `''`; since no live campaign
  //               carries a blank country_code, the effect is "nothing matches", which is
  //               the intended safety behaviour (auto-fill/dropdown must not offer values
  //               from other countries just because the mapping is missing).
  //   - `'US'`  → mapped → filter to that country.
  const countryCode = company ? getCountryCodeFromCompany(company, countryCompanyMapping) : null;
  const campaignName = excludeFields.has('Campaign Name') ? null : row['Campaign Name'];

  let filtered = productLine
    ? campaignsData.filter(
        (campaign) =>
          campaign.is_active && campaign.product_code === productLine && (countryCode === null || campaign.country_code === countryCode)
      )
    : campaignsData.filter((campaign) => campaign.is_active);

  if (campaignName && currentField !== 'Campaign Name') {
    filtered = filtered.filter((campaign) => campaign.product_line_campaign_name === campaignName);
  }

  // Apply the DMM_BUSINESS_SEGMENT_ORDER dependency chain — every field filled before
  // `currentField` (or all filled fields when `currentField` is null) must match,
  // skipping any that are themselves excluded (being corrected).
  const currentIndex =
    currentField && DMM_BUSINESS_SEGMENT_ORDER.includes(currentField as DMMBusinessSegmentType)
      ? DMM_BUSINESS_SEGMENT_ORDER.indexOf(currentField as DMMBusinessSegmentType)
      : DMM_BUSINESS_SEGMENT_ORDER.length;
  for (let i = 0; i < currentIndex; i++) {
    const previousField = DMM_BUSINESS_SEGMENT_ORDER[i];
    if (excludeFields.has(previousField)) continue;
    const selectedValue = row[previousField];
    if (selectedValue) {
      const campaignField = DMM_FIELDS_MAPPING[previousField];
      filtered = filtered.filter((campaign) => campaign[campaignField as keyof DMMProductLineCampaignFlatten] === selectedValue);
    }
  }

  return filtered;
};

/**
 * Context needed to compute dropdown values for a business segment cell.
 */
export interface BusinessSegmentDropdownContext {
  /** True for DMM group — enables campaign-based filtering. */
  isDMM: boolean;
  /** DMM master campaigns (empty for non-DMM). */
  campaignsData: DMMProductLineCampaignFlatten[];
  /** Country-company mapping (for DMM country-code derivation). */
  countryCompanyMapping: CountryCompanyMapping[];
  /** Master business segments (dropdown lists for non-DMM). */
  masterBusinessSegments: MasterBusinessSegments[];
  /** Expense types allowed for the current business group. */
  expenseTypesForCurrentGroup: string[];
  /**
   * Optional set of row fields that should be treated as "not yet decided" when
   * building the filter — i.e., their values do NOT constrain the dropdown.
   * The upload preview grid uses this so a cell that is itself flagged as an
   * error doesn't over-narrow its own or its neighbours' options while the
   * user is correcting them. The main grid leaves this empty.
   */
  excludeFields?: ReadonlySet<string>;
}

/**
 * Resolves the valid dropdown values for a business segment cell in the
 * current row context. Single source of truth used by both the main grid's
 * `agRichSelectCellEditor` params callback and the upload preview grid's
 * inline error-cell editor.
 *
 * For DMM:
 *   - Filters campaigns by Product Line, Company → Country Code, Campaign Name
 *     (each is skipped if the field is in `excludeFields`).
 *   - Applies the `DMM_BUSINESS_SEGMENT_ORDER` dependency chain from previous
 *     fields, again skipping fields listed in `excludeFields`.
 *   - Returns unique, sorted values for the target field pulled from the
 *     surviving campaign rows.
 *
 * For non-DMM: returns the master dropdown list for the field (or the group's
 * active expense types when the field is Expense Type).
 */
export const getBusinessSegmentDropdownValues = (
  field: string,
  rowData: Record<string, any>,
  ctx: BusinessSegmentDropdownContext
): string[] => {
  if (!ctx.isDMM) {
    if (field === BusinessSegmentNames.EXPENSE_TYPE) {
      return ctx.expenseTypesForCurrentGroup;
    }
    return ctx.masterBusinessSegments.find((segment) => segment.segment_name === field)?.business_segment_dropdown_list || [];
  }

  // DMM path — share the same filter chain as the auto-fill so the dropdown never
  // omits values the auto-fill just wrote (and vice versa).
  const filteredCampaigns = filterCampaignsForRow(rowData, field, ctx.campaignsData, ctx.countryCompanyMapping, ctx.excludeFields);
  return getDMMMasterSegmentDropDowns(filteredCampaigns, field);
};
